import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonVariant, ButtonSize } from '@/shared/ui/Button';

/**
 * FilterSidebarToggleButton
 *
 * A single, canonical toggle button that sits on the leading edge of the main
 * content area (absolute-positioned over the filter-sidebar border) and opens /
 * closes the filter sidebar on desktop screens.
 *
 * ## Design reference
 * - **Component**: `Button` with `ButtonVariant.Outline` + `ButtonSize.Xs`
 *   (mirrors RunStatusPage)
 * - **Positioning**: `top-[98px] -left-3` (mirrors DashboardPage)
 * - **Icon**: `ChevronLeft` 20 × 20, rotated 180° when closed (RunStatusPage pattern)
 * - **Shadow**: precise multi-layer shadow from RunStatusPage (not generic `shadow-lg`)
 * - **Visibility**: always hidden on mobile (`hidden`) — the mobile drawer has its own
 *   open trigger in `PageHeaderWithActions`. Shown as `flex` on lg+ (`lg:flex`).
 * - **data-testid**: always `"toggle-sidebar-button"` for consistent test targeting.
 *
 * ## Usage
 * ```tsx
 * <FilterSidebarToggleButton
 *   isOpen={isFiltersPanelOpen}
 *   onToggle={toggleSidebar}
 * />
 * ```
 *
 * Place it as the first child of the `<div className="flex-1 flex flex-col min-w-0 relative">`
 * main-content wrapper so the absolute positioning is relative to that container.
 */
export interface FilterSidebarToggleButtonProps {
  /** Whether the filter sidebar is currently open / expanded. */
  isOpen: boolean;
  /** Callback invoked when the button is clicked. Should toggle the sidebar state. */
  onToggle: () => void;
  /** Optional aria-label override. Defaults to "Close sidebar" / "Open sidebar". */
  ariaLabel?: string;
  /** Optional additional className applied to the Button. Use sparingly. */
  className?: string;
}

const FilterSidebarToggleButton: React.FC<FilterSidebarToggleButtonProps> = ({
  isOpen,
  onToggle,
  ariaLabel,
  className,
}) => {
  const { t } = useTranslation();
  const resolvedAriaLabel = ariaLabel ?? (isOpen ? t('global.closeSidebar') : t('global.openSidebar'));

  return (
    <Button
      variant={ButtonVariant.Outline}
      size={ButtonSize.Xs}
      onClick={onToggle}
      className={[
        // Hidden on mobile; flex on desktop
        'hidden lg:flex',
        // Absolute positioning: on the left edge of the main content area
        'absolute top-[98px] -left-3',
        // Centring transform
        'transform -translate-y-1/2',
        // Appearance
        'bg-surface border border-border rounded-full',
        // Precise shadow matching RunStatusPage
        'shadow-[0px_5px_8px_-4px_rgba(0,0,0,0.54),0_4px_6px_-4px_rgba(0,0,0,0.1)]',
        // Hover / focus
        'hover:border-border focus:outline-none',
        // Fixed size — override ButtonSize.Xs defaults via !important
        '!h-6 !w-6 !p-[3px]',
        // Stacking
        'z-50',
        // Flex alignment for the icon
        'items-center justify-center',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={resolvedAriaLabel}
      data-testid="toggle-sidebar-button"
    >
      <ChevronLeft
        className={`w-5 h-5 transition-transform ${!isOpen ? 'rotate-180' : ''}`}
        aria-hidden="true"
      />
    </Button>
  );
};

export default FilterSidebarToggleButton;